import { inject, Injectable } from '@angular/core';
import {
  ConvertLegacyFrequencyRequest,
  FileService,
  FrequencyConversionResult,
  FrequencyConverterService,
  FrequencyFileCheck,
  FrequencyMigrationReport,
  SiteFile,
  SiteFileKind,
  SiteFilesService,
  SiteFileUploadResult,
  SiteFrequencySetup,
} from '@wheere/api-site';
import { firstValueFrom } from 'rxjs';

/**
 * Appels sitemanager du fichier de fréquences (ADR-039, api-site 8.0.0) : état d'un site, dépôt
 * contrôlé (`dryRun`) ou stocké, et, réservés à PLATFORM_ADMIN, contrôle sans stockage,
 * convertisseur de l'ancien freq + theta et rapport de migration V41. Un refus 422
 * (`FrequencyFileRejection`) remonte tel quel : lu par `frequencyRejection`.
 */
@Injectable({ providedIn: 'root' })
export class SiteFrequencyClient {
  readonly #files = inject(SiteFilesService);
  readonly #legacyFiles = inject(FileService);
  readonly #converter = inject(FrequencyConverterService);

  /** `GET /sites/{id}/frequency-setup` : fichier, calib, antennes, signal.json, échec de migration. */
  setup(siteId: string): Promise<SiteFrequencySetup> {
    return firstValueFrom(this.#files.getSiteFrequencySetup(siteId));
  }

  /** `GET /sites/{id}/files` : contenus du fichier de fréquences et du calib (aperçu, téléchargement). */
  siteFiles(siteId: string): Promise<SiteFile[]> {
    return firstValueFrom(this.#legacyFiles.getAllFilesBySite(siteId));
  }

  /**
   * Dépôt contrôlé sans stockage (brouillon) contre `antennaCount` antennes. `frequencyCount` (calib
   * seulement) : nombre de fréquences du fichier de fréquences pas encore stocké, contre lequel
   * contrôler les lignes du calib (sinon le fichier en place).
   */
  checkSiteFile(
    siteId: string,
    kind: SiteFileKind,
    file: File,
    antennaCount: number,
    frequencyCount?: number
  ): Promise<SiteFileUploadResult> {
    return firstValueFrom(this.#files.uploadSiteFile(siteId, kind, file, true, antennaCount, frequencyCount));
  }

  /**
   * `DELETE /sites/{id}/files/{kind}` (site:configure-files) : 204 supprimé ou déjà absent. Sans
   * fichier de fréquences (ou sans calib), le site n'est plus prêt ; rien n'est publié avant la
   * prochaine validation.
   */
  deleteSiteFile(siteId: string, kind: SiteFileKind): Promise<unknown> {
    return firstValueFrom(this.#files.deleteSiteFile(siteId, kind));
  }

  /** `POST /frequency-files/check` (PLATFORM_ADMIN) : `nbFreq` contrôle les lignes d'un calib. */
  checkFile(file: File, kind: SiteFileKind, antennaCount?: number, nbFreq?: number): Promise<FrequencyFileCheck> {
    return firstValueFrom(this.#converter.checkFrequencyFile(file, kind, antennaCount, nbFreq));
  }

  /** `POST /frequency-files/convert` (PLATFORM_ADMIN) : ancien freq + theta → JSON, sans effet de bord. */
  convert(request: ConvertLegacyFrequencyRequest): Promise<FrequencyConversionResult> {
    return firstValueFrom(this.#converter.convertLegacyFrequency(request));
  }

  /** `GET /frequency-files/migration-report` (PLATFORM_ADMIN). */
  migrationReport(): Promise<FrequencyMigrationReport> {
    return firstValueFrom(this.#converter.getFrequencyMigrationReport());
  }
}
